import { Avatar, StatusPill } from '@payforge/ui';
import { findName } from '../../lib/org';
import type { OrgData } from '../../lib/org';
import { displayName, type EmployeeSearchResultDto } from '../../lib/types';

type Props = {
  listId: string;
  matches: EmployeeSearchResultDto[];
  loading: boolean;
  failed: boolean;
  activeIdx: number;
  org: OrgData | undefined;
  className: string;
  onHover: (idx: number) => void;
  onSelect: (employee: EmployeeSearchResultDto) => void;
};

// The result listbox of the global search bar, shared by the desktop dropdown and the mobile
// overlay (only their positioning classes differ). Presentational: every piece of state, including
// which row is active, stays owned by EmployeeSearch so keyboard navigation has one source of truth.
// Call sites cap the height and scroll, since the server returns up to 20 rows.
export function EmployeeSearchResults({
  listId,
  matches,
  loading,
  failed,
  activeIdx,
  org,
  className,
  onHover,
  onSelect,
}: Props) {
  return (
    <ul id={listId} role="listbox" aria-label="Employee search results" className={className}>
      {loading ? (
        <li role="option" aria-disabled="true" className="px-4 py-3 text-sm text-slate-500">
          Searching...
        </li>
      ) : failed ? (
        // A failed lookup must not read as an answer: "no employees found" about a colleague who
        // exists is worse than saying the search itself broke.
        <li role="option" aria-disabled="true" className="px-4 py-3 text-sm text-slate-500">
          Could not search right now
        </li>
      ) : matches.length === 0 ? (
        <li role="option" aria-disabled="true" className="px-4 py-3 text-sm text-slate-500">
          No employees found
        </li>
      ) : (
        matches.map((emp, idx) => {
          const jobTitle = findName(org?.jobTitles, emp.jobTitleId);
          const department = findName(org?.departments, emp.departmentId);
          const subtitle = [jobTitle, department].filter(Boolean).join(' · ');
          const isActive = idx === activeIdx;
          // Former employees are searchable on purpose, so a row must say so rather than look live.
          const terminated = emp.accountStatus === 'TERMINATED' || emp.terminationDate !== null;

          return (
            <li
              key={emp.id}
              id={`emp-search-opt-${idx}`}
              role="option"
              aria-selected={isActive}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(emp);
              }}
              onMouseEnter={() => onHover(idx)}
              className={`flex cursor-pointer items-center gap-3 px-4 py-2.5 text-sm ${
                isActive ? 'bg-slate-50' : 'hover:bg-slate-50'
              }`}
            >
              <Avatar
                name={displayName(emp)}
                photoUrl={emp.profilePictureUrl}
                hueKey={emp.id}
                size={32}
                variant="brand"
                inactive={terminated}
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-slate-900">{displayName(emp)}</span>
                {subtitle ? (
                  <span className="block truncate text-xs text-slate-500">{subtitle}</span>
                ) : null}
              </span>
              {terminated ? (
                <span className="shrink-0">
                  <StatusPill>Terminated</StatusPill>
                </span>
              ) : null}
            </li>
          );
        })
      )}
    </ul>
  );
}
